import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { membershipTiers } from '../data/memberships';
import { TierIcon } from './TierGlyphs';
const ease = [0.22, 1, 0.36, 1] as const;
export function Membership() {
  const [active, setActive] = useState(0);
  const reduceMotion = useReducedMotion();
  const tier = membershipTiers[active];
  return <section id="membership" aria-labelledby="membership-heading" className="bg-ink px-6 py-14 text-paper md:px-16 md:py-28" data-id="element-656" data-mp="1b0go857225jc">
      <div className="mx-auto grid max-w-[1320px] grid-cols-1 gap-8 md:gap-12 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-20" data-id="element-657" data-mp="1602pxev9a9yi">
        {/* Text column */}
        <div className="flex flex-col" data-id="element-658" data-mp="x0pckb40h18i">
          <div className="flex items-baseline justify-between gap-6" data-id="element-659" data-mp="x1qnmfl11nq8">
            <h2 id="membership-heading" className="font-editorial text-[34px] font-normal leading-[38px] tracking-[-0.01em] md:text-[44px] md:leading-[48px]" data-id="element-660" data-mp="2wdjqutyerpbe">
              A circle, kept small
            </h2>
            <p className="text-[14px] font-light text-paper/60" data-id="element-661" data-mp="ddgqohddyub4">By invitation only</p>
          </div>

          {/* Phones: the three tiers sit side by side as one compact row */}
          <ul role="tablist" aria-label="Membership tiers" className="mt-7 grid grid-cols-3 border-y border-paper/15 md:mt-10 md:block md:border-b-0 lg:mt-14" data-id="element-662" data-mp="4ed77cab4cw3">
            {membershipTiers.map((t, i) => {
            const isActive = i === active;
            return <li key={t.name} className="md:border-b md:border-paper/15" data-id="element-663" data-mp="112t0njgjf6eh" data-mpi={i}>
                  <button type="button" role="tab" id={`tier-tab-${i}`} aria-selected={isActive} aria-controls="tier-panel" onClick={() => setActive(i)} onMouseEnter={() => setActive(i)} onFocus={() => setActive(i)} className="group relative flex w-full items-center justify-between gap-6 py-3.5 text-left md:py-5" data-id="element-664" data-mp="ndhh0n9oaaot">
                    <span className={`flex items-center gap-1.5 transition-opacity duration-500 ease-luxe md:gap-4 ${isActive ? 'opacity-100' : 'opacity-35 group-hover:opacity-60'}`} data-id="element-665" data-mp="1pq0mqntqne6h">
                      <TierIcon tier={t.name} className="h-[15px] w-[15px] shrink-0 md:h-[34px] md:w-[34px]" data-id="element-666" data-mp="1bppzrvvjujql" />
                      <span className="font-editorial text-[24px] leading-[1.1] tracking-[-0.01em] md:text-[52px]" data-id="element-667" data-mp="16y7z78xful65">{t.name}</span>
                    </span>
                    <span className={`hidden h-px bg-paper transition-[width,opacity] duration-500 ease-luxe md:block ${isActive ? 'w-12 opacity-100' : 'w-0 opacity-0'}`} aria-hidden="true" data-id="element-668" data-mp="11x20muh3k3op" />
                    {/* Phones: a fine underline marks the chosen tier */}
                    <span className={`absolute -bottom-px left-0 h-px bg-paper transition-[width] duration-300 ease-luxe md:hidden ${isActive ? 'w-10' : 'w-0'}`} aria-hidden="true" data-id="element-669" data-mp="2zmpe0y301aqc" />
                  </button>
                </li>;
          })}
          </ul>

          <div id="tier-panel" role="tabpanel" aria-labelledby={`tier-tab-${active}`} className="mt-6 min-h-[118px] md:mt-10 md:min-h-[150px] lg:mt-auto" data-id="element-670" data-mp="2g0knt0fsntoh">
            <AnimatePresence mode="wait" initial={false} data-id="element-671" data-mp="2pxfhn614h5wf">
              <motion.div key={tier.name} initial={{
              opacity: 0,
              y: reduceMotion ? 0 : 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: reduceMotion ? 0 : -6
            }} transition={{
              duration: 0.45,
              ease
            }} data-id="element-672" data-mp="2pvumpfawbky1">
                <p className="max-w-[400px] text-[16px] font-light leading-[25px] text-paper/85 md:text-[18px] md:leading-[28px]" data-id="element-673" data-mp="225i85ll5apyh">{tier.line}</p>
                <a href={tier.href} className="group mt-5 inline-flex md:mt-7 items-center gap-4 border-b border-paper/40 pb-1.5 text-[16px] font-normal transition-colors duration-300 hover:border-paper" data-id="element-674" data-mp="3qkhwxdzgtd5">
                  {tier.cta}
                  <span aria-hidden="true" className="transition-transform duration-500 ease-luxe group-hover:translate-x-1" data-id="element-675" data-mp="reu9yh1sidcq">
                    →
                  </span>
                </a>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Framed image, crossfading per tier */}
        <div className="relative aspect-[16/10] overflow-hidden bg-paper/5 md:aspect-[4/3] lg:aspect-auto lg:min-h-[560px]" data-id="element-676" data-mp="vi0ar964vcbt">
          {membershipTiers.map((t, i) => <motion.img key={t.name} src={t.image} alt={i === active ? t.alt : ''} aria-hidden={i !== active} className="absolute inset-0 h-full w-full object-cover" initial={false} animate={{
          opacity: i === active ? 1 : 0,
          scale: reduceMotion ? 1 : i === active ? 1.04 : 1
        }} transition={{
          opacity: {
            duration: 1,
            ease
          },
          scale: {
            duration: 8,
            ease: 'linear'
          }
        }} data-id="element-677" data-mp="2x8ty4imd4xud" data-mpi={i} />)}
          <div className="film-grain pointer-events-none absolute inset-[-50%] opacity-[0.06] motion-reduce:hidden" aria-hidden="true" data-id="element-678" data-mp="tz8q0eepd53f" />
        </div>
      </div>
    </section>;
}